/**
 * /portal/password-reset - request-a-reset-link form.
 *
 * Always returns 200 regardless of whether the email exists (server-
 * side email-enumeration defense, mirrors the admin
 * /v1/auth/password-reset-request route). UI surfaces a generic
 * "If that email is on file you'll receive a link shortly" message on
 * success.
 *
 * This page only handles the request-a-link side. The token-consume
 * step lives in the sibling route `/portal/password-reset/confirm`
 * (PasswordResetConfirmPage), backed by
 * `POST /v1/portal/auth/password-reset/confirm` ->
 * portalUsers.completePasswordReset.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { OrbotoLockup } from '../components/OrbotoLockup';
import { LegalFooter } from '../components/LegalFooter';

export function PasswordResetPage() {
  const { t } = useTranslation('password-reset');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api('/v1/portal/auth/password-reset/request', {
        method: 'POST',
        body: { email: email.trim().toLowerCase() },
      });
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(t('request.error.tooMany'));
      } else {
        setError(t('request.error.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-gray-800 px-4 text-gray-900 dark:text-gray-100">
      <div className="w-full max-w-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-2">
          <OrbotoLockup className="h-7 w-auto" />
          <div className="text-center text-xs text-gray-500">{t('request.header')}</div>
        </div>

        {sent ? (
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <p>
              <Trans t={t} i18nKey="request.sent" values={{ email }} components={{ strong: <strong /> }} />
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700"
            >
              {t('request.backToSignIn')}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('request.intro')}
            </p>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-700 dark:text-gray-300">{t('request.email')}</span>
              <input
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
            </label>

            {error && (
              <div
                role="alert"
                className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {submitting ? t('request.submitting') : t('request.submit')}
            </button>

            <Link
              to="/login"
              className="text-center text-sm text-gray-500 hover:text-brand-500"
            >
              {t('request.backToSignIn')}
            </Link>
          </form>
        )}
        <LegalFooter className="mt-8" />
      </div>
    </div>
  );
}
